import type { EmpAudience } from "./access";
import type { EmpAudienceLabel } from "./feed";
import type { EmpVideo } from "./media";

/**
 * Resources and policies (Phase 6). See
 * docs/superpowers/plans/2026-10-02-skyemployee-resources.md. Every resource
 * has a named owner and a review month; one that requires acknowledgement is
 * acknowledged per version, in the app (no signatures, decision 18).
 */

/** What a resource is. A video is uploaded (MP4 or WebM), or can still be a link. */
export type EmpResourceKind = "document" | "image" | "link" | "video";

/** The checked type of an uploaded file (by its bytes, never its name). */
export type EmpResourceFileType = "pdf" | "docx" | "xlsx" | "pptx" | "jpeg" | "png" | "webp";

export const RESOURCE_FILE_LABELS: Record<EmpResourceFileType, string> = {
  pdf: "PDF",
  docx: "Word",
  xlsx: "Excel",
  pptx: "PowerPoint",
  jpeg: "JPEG image",
  png: "PNG image",
  webp: "WebP image",
};

/** The most resources My Resources lists at once; search or a category finds the rest. */
export const RESOURCE_READER_LIMIT = 300;
export const RESOURCE_TITLE_MAX = 120;
export const RESOURCE_DESCRIPTION_MAX = 500;
export const RESOURCE_CATEGORY_NAME_MAX = 60;
export const RESOURCE_LINK_MAX = 2000;
/** One file at a time, up to 25 MB (P6-2). */
export const RESOURCE_FILE_MAX_BYTES = 25 * 1024 * 1024;
/** Days to acknowledge each version, from when it's published (P6-7). */
export const ACK_DUE_DAYS_DEFAULT = 14;
export const ACK_DUE_DAYS_MAX = 90;
/** A new resource is due for review this many months ahead (P6-11). */
export const REVIEW_MONTHS_DEFAULT = 12;
/** A review month, e.g. "2027-03". */
export const REVIEW_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

export interface EmpNamedPerson {
  partyId: string;
  name: string;
}

/** A library category; every category has a named owner (P6-11). */
export interface EmpResourceCategory {
  id: string;
  name: string;
  owner: EmpNamedPerson | null;
  /** Resources in it, archived ones included. */
  resourceCount: number;
}

export interface EmpResourceCategoryInput {
  name: string;
  ownerPartyId: string;
}

/** A resource's details, as an Admin sets them. The file or link is sent separately. */
export interface EmpResourceInput {
  title: string;
  description: string;
  categoryId: string;
  /** Null: the category's owner. */
  ownerPartyId: string | null;
  audience: EmpAudience;
  requiresAck: boolean;
  /** Days to acknowledge each version; null when not required. */
  ackDueDays: number | null;
  /** "YYYY-MM". */
  reviewMonth: string;
}

/** A resource as Administration → Resource library shows it. */
export interface EmpResourceAdmin {
  id: string;
  title: string;
  description: string;
  category: { id: string; name: string };
  kind: EmpResourceKind;
  /** Files only. */
  fileType: EmpResourceFileType | null;
  fileName: string | null;
  sizeBytes: number | null;
  /** Links only. */
  linkUrl: string | null;
  /** Videos only. Optional: an older server doesn't send it. */
  video?: EmpVideo | null;
  owner: EmpNamedPerson | null;
  audience: EmpAudience;
  audienceLabel: EmpAudienceLabel;
  requiresAck: boolean;
  ackDueDays: number | null;
  version: number;
  versionPublishedAt: string;
  reviewMonth: string;
  /** Its review month has passed. */
  pastReview: boolean;
  archived: boolean;
  /** Ever acknowledged (any version): it can only be archived, not deleted. */
  acknowledged: boolean;
  /** The cover shown on its tile, as for a reader. */
  coverUrl: string | null;
  /** 'upload' survives a new version; 'render' is redrawn from the first page. */
  coverSource: "upload" | "render" | null;
}

// ── Reading and acknowledging (slice 6B) ───────────────────────────────────

/** My state for a resource that requires acknowledgement. */
export interface EmpResourceAck {
  /** When I acknowledged the current version; null while outstanding. */
  acknowledgedAt: string | null;
  /** "YYYY-MM-DD": the current version's due date for me. */
  dueOn: string;
}

/** A resource as the person reading it sees it (in their audience, not archived). */
export interface EmpResource {
  id: string;
  title: string;
  description: string;
  category: { id: string; name: string };
  kind: EmpResourceKind;
  fileType: EmpResourceFileType | null;
  fileName: string | null;
  /** Files only. Shown on a tile so somebody can judge whether to start it now. */
  sizeBytes: number | null;
  linkUrl: string | null;
  /** Videos only; played through GET /videos/:id/playback. */
  video?: EmpVideo | null;
  owner: EmpNamedPerson | null;
  version: number;
  versionPublishedAt: string;
  reviewMonth: string;
  favourite: boolean;
  /**
   * The tile's cover, served from GET /resources/:id/cover, or null when
   * there is none and the reader should draw one from the title.
   *
   * An image resource has no separate cover: the file IS the picture, and
   * the tile uses the file endpoint for it.
   */
  coverUrl: string | null;
  /** Null when it doesn't require acknowledgement. */
  ack: EmpResourceAck | null;
}

export type EmpResourceView = "library" | "favourites" | "acknowledge";

/** A category with how many resources I can see in it. */
export interface EmpReaderCategory {
  id: string;
  name: string;
  count: number;
}

// ── Tracking and review (slice 6C) ─────────────────────────────────────────

/** A resource that requires acknowledgement, with its rate among the people in view. */
export interface EmpAckTrackerRow {
  resourceId: string;
  title: string;
  /**
   * What it is, because it decides what the evidence can mean: a link opens
   * on someone else's site, so there is nothing for us to witness and the
   * ledger must say that rather than report everyone as having confirmed
   * without reading.
   */
  kind: EmpResourceKind;
  categoryName: string;
  owner: string | null;
  version: number;
  /** "YYYY-MM-DD": the current version's due date (later for people who started after it). */
  dueOn: string;
  /** Everyone it's for, among the people in view. */
  total: number;
  acknowledged: number;
  /** Not yet acknowledged and past their own due date. */
  overdue: number;
}

/**
 * What the server saw this person do, alongside their acknowledgement.
 *
 * Deliberately counts and times rather than a verdict. Opening a file is the
 * only reading the server can honestly claim to have witnessed: someone who
 * opens a policy and closes it two seconds later looks the same here as
 * someone who studies it, and a ledger that turned this into "read" would be
 * making a claim the data doesn't support. What it CAN show plainly is the
 * case a manager actually needs to spot — confirmed without ever opening it.
 */
export interface EmpAckEvidence {
  /** Times the file endpoint served it to them, for this version. */
  opens: number;
  /** First and last open, or null where they never opened it. */
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  /** Seconds between the first open and confirming; null if never opened. */
  secondsBeforeConfirming: number | null;
}

/** One person on a resource's tracker. */
export interface EmpAckPerson {
  partyId: string;
  name: string;
  /** Tells apart two people with the same name. */
  email: string | null;
  locationName: string | null;
  acknowledgedAt: string | null;
  dueOn: string;
  /**
   * Present once they have acknowledged. A resource nobody can open — a link
   * that lives on someone else's site — has nothing to witness, so this is
   * all zeroes there and the ledger says so rather than implying neglect.
   */
  evidence: EmpAckEvidence | null;
}

/** People shown on screen for one resource; the CSV export has everyone. */
export const ACK_DETAIL_PEOPLE_MAX = 2000;

export interface EmpAckTrackerDetail {
  row: EmpAckTrackerRow;
  /** Not yet acknowledged first; at most ACK_DETAIL_PEOPLE_MAX. */
  people: EmpAckPerson[];
  /** True when there were more people than are listed. */
  truncated?: boolean;
}

/** A resource past its review month, for Administration → Resource library. */
export interface EmpPastReview {
  id: string;
  title: string;
  categoryName: string;
  owner: EmpNamedPerson | null;
  reviewMonth: string;
  /** The owner was emailed about this review month. */
  ownerReminded: boolean;
}
